import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, EyeOff, ListPlus, Plus, SkipForward, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { btnGhost, errMsg, inp } from "@/components/attendance/ui";

const today = () => new Date().toISOString().slice(0, 10);
const TYPE = { hymn: "Hymn", bible: "Bible", announcement: "Notice", song: "Special", blank: "Blank" };
const strip = (i) => ({ type: i.type || "hymn", hymn_id: i.hymn_id || null, ref: i.ref || null, item_id: i.item_id || null, label: i.title || i.label || "" });

export default function ProgrammePanel({ current, canEdit, onOpen }) {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [pos, setPos] = useState(-1);
  const prog = list.find((p) => p.id === sel);
  useEffect(() => { api.get("/projection/programmes").then((r) => { setList(r.data); if (r.data[0]) setSel(r.data[0].id); }).catch(() => {}); }, []);

  const save = async (p, items) => {
    try {
      const r = await api.put(`/projection/programmes/${p.id}`, { name: p.name, date: p.date, items: items.map(strip) });
      setList(list.map((x) => (x.id === p.id ? r.data : x)));
    } catch (e) { toast.error(errMsg(e, "Could not save programme")); }
  };
  const create = async () => {
    const name = window.prompt("Programme name", `Sunday Service ${today()}`);
    if (!name) return;
    try { const r = await api.post("/projection/programmes", { name, date: today(), items: [] }); setList([r.data, ...list]); setSel(r.data.id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!prog || !window.confirm(`Delete programme “${prog.name}”?`)) return;
    await api.delete(`/projection/programmes/${prog.id}`).catch(() => {});
    setList(list.filter((x) => x.id !== prog.id)); setSel(null);
  };
  const move = (i, d) => { const it = [...prog.items]; [it[i], it[i + d]] = [it[i + d], it[i]]; save(prog, it); };
  const add = (item) => { if (!item) { toast("Open a hymn, passage, announcement or special number first"); return; } save(prog, [...prog.items, item]); };
  const open = (i) => { setPos(i); onOpen(prog.items[i]); };
  const nextItem = prog?.items[pos + 1];

  return (
    <div className="space-y-3" data-testid="programme-panel">
      <div className="flex gap-2">
        <select className={inp} value={sel || ""} onChange={(e) => { setSel(e.target.value); setPos(-1); }} data-testid="programme-select">
          <option value="">{list.length ? "Choose a programme…" : "No programmes yet"}</option>
          {list.map((p) => <option key={p.id} value={p.id}>{p.date ? `${p.date} · ` : ""}{p.name}</option>)}
        </select>
        {canEdit && <button onClick={create} title="New programme" className="px-3 rounded border border-white/15 text-neutral-200 hover:border-[#D4AF37]" data-testid="programme-new"><Plus size={14} /></button>}
        {canEdit && prog && <button onClick={remove} title="Delete programme" className="px-3 rounded border border-white/15 text-neutral-400 hover:text-red-400" data-testid="programme-delete"><Trash2 size={14} /></button>}
      </div>
      {prog && (
        <>
          {canEdit && (
            <div className="flex gap-2">
              <button onClick={() => add(current)} className={`${btnGhost} flex-1`} data-testid="programme-add-current"><ListPlus size={14} /> Add current</button>
              <button onClick={() => add({ type: "blank", label: window.prompt("Label (e.g. Opening Prayer)", "Prayer") || "Blank screen" })} className={btnGhost} title="Add a blank-screen moment" data-testid="programme-add-blank"><EyeOff size={14} /></button>
            </div>
          )}
          <button onClick={() => nextItem && open(pos + 1)} disabled={!nextItem} className={`${btnGhost} w-full justify-between`} data-testid="programme-next">
            <span className="truncate">{nextItem ? `Next: ${nextItem.title}` : "End of programme"}</span><SkipForward size={14} />
          </button>
          <ol className="space-y-1 max-h-[46vh] overflow-y-auto" data-testid="programme-items">
            {prog.items.map((it, i) => (
              <li key={`${i}-${it.title}`} className={`flex items-center gap-2 rounded-lg px-2 py-2 border ${i === pos ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/5 bg-white/[0.02]"}`} data-testid={`programme-item-${i}`}>
                <span className="text-[9px] tracking-wider uppercase text-neutral-500 w-12 shrink-0">{TYPE[it.type || "hymn"]}</span>
                <button onClick={() => open(i)} className="flex-1 text-left min-w-0 truncate" data-testid={`programme-open-${i}`}>
                  {(it.type || "hymn") === "hymn" && <span className="font-serif-display text-lg text-[#D4AF37] mr-2">{it.cac_number}</span>}
                  <span className="text-xs text-white">{it.title}</span>
                </button>
                {canEdit && <>
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="text-neutral-500 hover:text-white disabled:opacity-20" title="Move up" data-testid={`programme-up-${i}`}><ArrowUp size={12} /></button>
                  <button disabled={i === prog.items.length - 1} onClick={() => move(i, 1)} className="text-neutral-500 hover:text-white disabled:opacity-20" title="Move down" data-testid={`programme-down-${i}`}><ArrowDown size={12} /></button>
                  <button onClick={() => save(prog, prog.items.filter((_, j) => j !== i))} className="text-neutral-500 hover:text-red-400" title="Remove" data-testid={`programme-remove-${i}`}><X size={12} /></button>
                </>}
              </li>
            ))}
            {!prog.items.length && <li className="text-xs text-neutral-500 px-1">Open any content, then click “Add current”.</li>}
          </ol>
        </>
      )}
    </div>
  );
}
